import React, { useId } from 'react';

/**
 * Polished macOS Big-Sur-style app icons — all original inline SVG, no
 * third-party glyph copied (IP-clean by construction). Each icon is a 28×28
 * squircle with:
 *   - a vertical gradient (lighter top → richer bottom),
 *   - a glossy specular highlight clipped to the squircle's top half,
 *   - a subtle 1px inner border for depth,
 *   - a soft outer drop-shadow (via CSS `filter`),
 *   - a centered white (or two-tone) glyph drawn over the squircle.
 *
 * To add an icon, append an entry to APP_ICON_DEFS with:
 *   - top:   the lighter hue (gradient top stop)
 *   - bot:   the deeper hue  (gradient bottom stop + any glyph accents)
 *   - glyph: JSX nodes drawn inside the 28×28 viewBox after the squircle
 *
 * Usage:
 *   <AppIcon name="dashboard" size={22} />
 */

const APP_ICON_DEFS = {
  // ── Dashboard: 2×2 grid of squares ───────────────────────────────────────
  dashboard: {
    top: '#60a5fa', bot: '#2563eb',
    glyph: (
      <g fill="#ffffff">
        <rect x="6"  y="6"  width="7" height="7" rx="1.5" />
        <rect x="15" y="6"  width="7" height="7" rx="1.5" />
        <rect x="6"  y="15" width="7" height="7" rx="1.5" />
        <rect x="15" y="15" width="7" height="7" rx="1.5" />
      </g>
    ),
  },

  // ── MedTransact medical/business modules (IP-clean original glyphs) ───────

  // Medical group — caduceus-free medical cross.
  medical: {
    top: '#f89a4e', bot: '#F27D26',
    glyph: (
      <g fill="#ffffff">
        <rect x="12" y="6" width="4" height="16" rx="1.2" />
        <rect x="6" y="12" width="16" height="4" rx="1.2" />
      </g>
    ),
  },

  // Provider — hospital building with a cross.
  provider: {
    top: '#7dd3fc', bot: '#0284c7',
    glyph: (
      <g fill="#ffffff">
        <rect x="7" y="8" width="14" height="14" rx="1.4" />
        <rect x="13" y="11" width="2" height="6" fill="#0284c7" />
        <rect x="11" y="13" width="6" height="2" fill="#0284c7" />
      </g>
    ),
  },

  // Diagnosis — heartbeat pulse line.
  diagnosis: {
    top: '#fda4af', bot: '#e11d48',
    glyph: (
      <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 6 14 H 10 L 12 9 L 16 19 L 18 14 H 22" />
      </g>
    ),
  },

  // CPT — price/procedure tag.
  cpt: {
    top: '#c4b5fd', bot: '#7c3aed',
    glyph: (
      <g fill="none" stroke="#ffffff" strokeWidth="1.9" strokeLinejoin="round">
        <path d="M 14 6 L 22 6 L 22 14 L 13 23 L 5 15 Z" fill="none" />
        <circle cx="18" cy="10" r="1.5" fill="#ffffff" stroke="none" />
      </g>
    ),
  },

  // Physician — stethoscope (tube + bell).
  physician: {
    top: '#6ee7b7', bot: '#059669',
    glyph: (
      <g fill="none" stroke="#ffffff" strokeWidth="1.9" strokeLinecap="round">
        <path d="M 9 6 V 11 a 4 4 0 0 0 8 0 V 6" />
        <path d="M 13 18 a 5 5 0 0 0 5 -5" />
        <circle cx="18" cy="12.5" r="2.1" fill="#059669" />
      </g>
    ),
  },

  // Corporate — office building with windows.
  corporate: {
    top: '#fcd34d', bot: '#d97706',
    glyph: (
      <g fill="#ffffff">
        <rect x="8" y="6" width="12" height="16" rx="1.2" />
        <g fill="#d97706">
          <rect x="10.5" y="9"  width="2.2" height="2.2" />
          <rect x="15.3" y="9"  width="2.2" height="2.2" />
          <rect x="10.5" y="13" width="2.2" height="2.2" />
          <rect x="15.3" y="13" width="2.2" height="2.2" />
          <rect x="12.8" y="17" width="2.4" height="5" />
        </g>
      </g>
    ),
  },

  // Plan Setup — checklist document.
  planSetup: {
    top: '#a5b4fc', bot: '#4f46e5',
    glyph: (
      <g>
        <rect x="7" y="5.5" width="14" height="17" rx="1.6" fill="#ffffff" />
        <g stroke="#4f46e5" strokeWidth="1.5" strokeLinecap="round" fill="none">
          <path d="M 10 10 l 1.4 1.4 l 2.4 -2.6" />
          <path d="M 10 15.5 l 1.4 1.4 l 2.4 -2.6" />
          <line x1="15.5" y1="10.3" x2="18.5" y2="10.3" />
          <line x1="15.5" y1="15.8" x2="18.5" y2="15.8" />
        </g>
      </g>
    ),
  },

  // Member — ID badge with a person.
  member: {
    top: '#5eead4', bot: '#0d9488',
    glyph: (
      <g>
        <rect x="6.5" y="7" width="15" height="14" rx="1.8" fill="#ffffff" />
        <circle cx="11.5" cy="12.5" r="2.2" fill="#0d9488" />
        <path d="M 8 18.5 c 0 -2 1.6 -3.2 3.5 -3.2 s 3.5 1.2 3.5 3.2 Z" fill="#0d9488" />
        <g fill="#0d9488"><rect x="16.5" y="11" width="3.5" height="1.5" rx="0.5" /><rect x="16.5" y="14" width="3.5" height="1.5" rx="0.5" /></g>
      </g>
    ),
  },

  // Payments — banknote with a peso mark.
  payments: {
    top: '#86efac', bot: '#16a34a',
    glyph: (
      <g>
        <rect x="5" y="9" width="18" height="11" rx="1.6" fill="#ffffff" />
        <circle cx="14" cy="14.5" r="2.6" fill="none" stroke="#16a34a" strokeWidth="1.4" />
        <text x="14" y="17.4" fontSize="6" fontWeight="700" textAnchor="middle" fill="#16a34a" fontFamily="Helvetica, Arial">₱</text>
      </g>
    ),
  },

  // MIS Report — bar chart.
  misReport: {
    top: '#fdba74', bot: '#ea580c',
    glyph: (
      <g fill="#ffffff">
        <rect x="7"  y="14" width="3.2" height="8"  rx="0.8" />
        <rect x="12.4" y="10" width="3.2" height="12" rx="0.8" />
        <rect x="17.8" y="7"  width="3.2" height="15" rx="0.8" />
      </g>
    ),
  },

  // ── OAuth Users: head + shoulders silhouette ─────────────────────────────
  oauthUsers: {
    top: '#a5b4fc', bot: '#6366f1',
    glyph: (
      <g fill="#ffffff">
        <circle cx="14" cy="10.5" r="3.4" />
        <path d="M 6.5 22.5 C 6.5 18.4 9.8 15.5 14 15.5 S 21.5 18.4 21.5 22.5 Z" />
      </g>
    ),
  },

  // ── OAuth Clients: a key (ring + shaft + 2 teeth) ────────────────────────
  oauthClients: {
    top: '#fbbf24', bot: '#d97706',
    glyph: (
      <g fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="11.5" cy="11" r="3.6" />
        <line x1="14"   y1="13.6" x2="21" y2="20.6" />
        <line x1="18"   y1="18.6" x2="20" y2="16.6" />
        <line x1="15.5" y1="16.1" x2="17.5" y2="14.1" />
      </g>
    ),
  },

  // ── Share Token: two interlocking chain links ────────────────────────────
  shareToken: {
    top: '#5eead4', bot: '#0d9488',
    glyph: (
      <g fill="none" stroke="#ffffff" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 11 10 L 9 12 a 4 4 0 0 0 0 5.6 a 4 4 0 0 0 5.6 0 L 16 16" />
        <path d="M 17 18 L 19 16 a 4 4 0 0 0 0 -5.6 a 4 4 0 0 0 -5.6 0 L 12 12" />
      </g>
    ),
  },

  // ── Batch Upload: tray with up-arrow ─────────────────────────────────────
  batchUpload: {
    top: '#c4b5fd', bot: '#7c3aed',
    glyph: (
      <g fill="#ffffff">
        <path d="M 14 6 L 20 12 H 17 V 18 H 11 V 12 H 8 Z" />
        <rect x="6" y="20" width="16" height="2.2" rx="1" />
      </g>
    ),
  },

  // ── Chat: speech bubble with bottom-left tail ────────────────────────────
  chat: {
    top: '#6ee7b7', bot: '#10b981',
    glyph: (
      <path
        d="M 6 7 H 22 C 23.1 7 24 7.9 24 9 V 18 C 24 19.1 23.1 20 22 20 H 14 L 10 23 V 20 H 6 C 4.9 20 4 19.1 4 18 V 9 C 4 7.9 4.9 7 6 7 Z"
        fill="#ffffff"
      />
    ),
  },

  // ── Bloom Stranger: brand-image override (Rule 75 brand asset) ──────────
  // Renders the CRA default `public/logo192.png` so projects can swap the
  // file once with their actual Bloom branding and every nav surface
  // updates automatically. The `imgSrc` field is the contract: when set,
  // AppIcon composites the image INSIDE the polished squircle (gradient
  // background + gloss + drop-shadow + inner border) via `LogoBadge` — the
  // same framed look every other AppIcon has, but with the logo as the
  // "glyph" instead of an inline SVG. `top`/`bot` tint the brand frame
  // (pink to match the Bloom palette). To restore a pure inline SVG glyph,
  // drop `imgSrc` and supply `top/bot/glyph` instead.
  bloomStranger: {
    imgSrc: '/logo192.png',
    alt:    'Bloom Stranger',
    top:    '#f9a8d4', bot: '#db2777',
  },

  // ── Bloom Wallet (Rule 76): pink-gradient wallet with a bloom flower ─────
  // Squircle in the brand pink palette (matches the Send-Bloom modal
  // gradient) with a stylized wallet body + a 5-petal bloom rosette as the
  // clasp. IP-clean: all paths hand-authored inline SVG, no third-party.
  bloomWallet: {
    top: '#f9a8d4', bot: '#db2777',
    glyph: (
      <g>
        {/* Wallet body */}
        <path
          d="M 5 9 H 21 C 22.1 9 23 9.9 23 11 V 21 C 23 22.1 22.1 23 21 23 H 7 C 5.9 23 5 22.1 5 21 V 9 Z"
          fill="#ffffff"
          opacity="0.96"
        />
        {/* Wallet flap shadow */}
        <path
          d="M 5 9 V 7.5 C 5 6.7 5.7 6 6.5 6 H 18.5 C 19.3 6 20 6.7 20 7.5 V 9 Z"
          fill="#ffffff"
          opacity="0.85"
        />
        {/* Bloom-flower clasp (5 petals + center) */}
        <g transform="translate(18 17)">
          <circle cx="0" cy="0" r="1.3" fill="#db2777" />
          <ellipse cx="0"   cy="-2.2" rx="1.1" ry="1.6" fill="#db2777" />
          <ellipse cx="0"   cy="2.2"  rx="1.1" ry="1.6" fill="#db2777" />
          <ellipse cx="-2.1" cy="0"   rx="1.6" ry="1.1" fill="#db2777" />
          <ellipse cx="2.1"  cy="0"   rx="1.6" ry="1.1" fill="#db2777" />
        </g>
      </g>
    ),
  },

  // ── Admin: shield + checkmark inside ─────────────────────────────────────
  admin: {
    top: '#fca5a5', bot: '#dc2626',
    glyph: (
      <g>
        <path
          d="M 14 4 L 22 7 V 13 C 22 18 18 22 14 24 C 10 22 6 18 6 13 V 7 Z"
          fill="#ffffff"
        />
        <polyline
          points="10,13 13,16 18,11"
          fill="none" stroke="#dc2626" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        />
      </g>
    ),
  },

  // ── Tools: wrench at 45° ─────────────────────────────────────────────────
  tools: {
    top: '#94a3b8', bot: '#475569',
    glyph: (
      <g fill="#ffffff">
        <path d="M 20 8 a 4 4 0 0 1 -5 5 L 8 20 L 5 23 L 4 22 L 7 19 L 14 12 a 4 4 0 0 1 5 -5 Z" />
        <circle cx="17" cy="9" r="1.4" fill="#475569" />
      </g>
    ),
  },

  // ── Folder: classic tab + body ───────────────────────────────────────────
  folder: {
    top: '#fde047', bot: '#ca8a04',
    glyph: (
      <path
        d="M 4 9 H 11 L 13 7 H 22 C 23.1 7 24 7.9 24 9 V 19 C 24 20.1 23.1 21 22 21 H 6 C 4.9 21 4 20.1 4 19 Z"
        fill="#ffffff"
      />
    ),
  },

  // ── Audit Logs: document with horizontal lines ───────────────────────────
  auditLogs: {
    top: '#d8b4fe', bot: '#9333ea',
    glyph: (
      <g>
        <path
          d="M 8 4 H 17 L 21 8 V 24 C 21 24.55 20.55 25 20 25 H 8 C 7.45 25 7 24.55 7 24 V 5 C 7 4.45 7.45 4 8 4 Z"
          fill="#ffffff"
        />
        <g stroke="#9333ea" strokeWidth="1.4" strokeLinecap="round">
          <line x1="10" y1="12" x2="18" y2="12" />
          <line x1="10" y1="15" x2="18" y2="15" />
          <line x1="10" y1="18" x2="15" y2="18" />
        </g>
      </g>
    ),
  },

  // ── Outbound HTTP: two arrows (one up-right, one down-left) ──────────────
  outboundHttp: {
    top: '#7dd3fc', bot: '#0ea5e9',
    glyph: (
      <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6,11 22,11" />
        <polyline points="18,7 22,11 18,15" />
        <polyline points="22,18 6,18" />
        <polyline points="10,22 6,18 10,14" />
      </g>
    ),
  },

  // ── Field History: clock face with hands ─────────────────────────────────
  fieldHistory: {
    top: '#fdba74', bot: '#ea580c',
    glyph: (
      <g>
        <circle cx="14" cy="14" r="8" fill="#ffffff" />
        <g stroke="#ea580c" strokeWidth="1.8" strokeLinecap="round">
          <line x1="14" y1="14" x2="14" y2="9" />
          <line x1="14" y1="14" x2="18" y2="14" />
        </g>
      </g>
    ),
  },

  // ── Login Attempts: arrow entering a door ────────────────────────────────
  loginAttempts: {
    top: '#fda4af', bot: '#e11d48',
    glyph: (
      <g fill="#ffffff">
        <path d="M 13 5 H 22 C 23 5 24 6 24 7 V 21 C 24 22 23 23 22 23 H 13 V 20 H 21 V 8 H 13 Z" />
        <path
          d="M 5 14 L 16 14 M 12 10 L 16 14 L 12 18"
          fill="none" stroke="#ffffff" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        />
      </g>
    ),
  },

  // ── Chat Messages: bubble with three dots inside ─────────────────────────
  chatMessages: {
    top: '#d9f99d', bot: '#65a30d',
    glyph: (
      <g>
        <path
          d="M 6 7 H 22 C 23.1 7 24 7.9 24 9 V 18 C 24 19.1 23.1 20 22 20 H 14 L 10 23 V 20 H 6 C 4.9 20 4 19.1 4 18 V 9 C 4 7.9 4.9 7 6 7 Z"
          fill="#ffffff"
        />
        <g fill="#65a30d">
          <circle cx="10" cy="13.5" r="1.2" />
          <circle cx="14" cy="13.5" r="1.2" />
          <circle cx="18" cy="13.5" r="1.2" />
        </g>
      </g>
    ),
  },

  // ── Webhook Deliveries: paper airplane ───────────────────────────────────
  webhookDeliveries: {
    top: '#67e8f9', bot: '#0891b2',
    glyph: (
      <g>
        <path d="M 23 5 L 5 12 L 12 14 L 14 22 Z" fill="#ffffff" />
        <path d="M 12 14 L 23 5" stroke="#0891b2" strokeWidth="1.4" strokeLinecap="round" />
      </g>
    ),
  },

  // ── Failed Jobs: warning triangle with ! ─────────────────────────────────
  failedJobs: {
    top: '#fcd34d', bot: '#d97706',
    glyph: (
      <g>
        <path
          d="M 14 4.5 L 24 22 H 4 Z"
          fill="#ffffff"
        />
        <g fill="#d97706">
          <rect x="13.2" y="11" width="1.6" height="6" rx="0.8" />
          <circle cx="14" cy="19.5" r="1.1" />
        </g>
      </g>
    ),
  },

  // ── Notification: bell with clapper (warm amber — "alert/attention") ─────
  notification: {
    top: '#fcd34d', bot: '#f59e0b',
    glyph: (
      <g fill="#ffffff">
        <path d="M 14 5 a 5.6 5.6 0 0 0 -5.6 5.6 v 3.4 c 0 1.4 -0.5 2.7 -1.4 3.8 L 5.8 19.2 h 16.4 l -1.2 -1.4 c -0.9 -1.1 -1.4 -2.4 -1.4 -3.8 v -3.4 A 5.6 5.6 0 0 0 14 5 Z" />
        <path d="M 11.6 21 a 2.4 2.4 0 0 0 4.8 0 Z" />
      </g>
    ),
  },

  // ── Theme Sun: filled disc + 8 rays (warm gold — "light mode") ───────────
  themeSun: {
    top: '#fde047', bot: '#f59e0b',
    glyph: (
      <g>
        <circle cx="14" cy="14" r="4.6" fill="#ffffff" />
        <g
          fill="none" stroke="#ffffff" strokeWidth="2"
          strokeLinecap="round"
        >
          <line x1="14"   y1="4.5"  x2="14"   y2="7" />
          <line x1="14"   y1="21"   x2="14"   y2="23.5" />
          <line x1="4.5"  y1="14"   x2="7"    y2="14" />
          <line x1="21"   y1="14"   x2="23.5" y2="14" />
          <line x1="7.2"  y1="7.2"  x2="9"    y2="9" />
          <line x1="19"   y1="19"   x2="20.8" y2="20.8" />
          <line x1="7.2"  y1="20.8" x2="9"    y2="19" />
          <line x1="19"   y1="9"    x2="20.8" y2="7.2" />
        </g>
      </g>
    ),
  },

  // ── Theme Moon: crescent (cool indigo — "dark mode") ─────────────────────
  themeMoon: {
    top: '#a5b4fc', bot: '#4338ca',
    glyph: (
      <path
        d="M 21 16 a 8 8 0 1 1 -10 -10 a 6.4 6.4 0 0 0 10 10 Z"
        fill="#ffffff"
      />
    ),
  },

  // ── Voice Call: classic phone handset, tilted, with a soft inner cradle ──
  // Two-tone — white handset glyph on an emerald gradient (matches the chat
  // family so voice/video read as "from the chat module"). The inner cradle
  // line is the bottom gradient hue, picking up the chat green for cohesion.
  voiceCall: {
    top: '#6ee7b7', bot: '#059669',
    glyph: (
      <g>
        <path
          d="M 8.4 5.4 L 11.4 6.6 C 12.2 6.9 12.6 7.8 12.3 8.6 L 11.3 11.3 C 11.1 11.9 11.3 12.6 11.8 13 L 15 16.2 C 15.4 16.6 16.1 16.8 16.7 16.6 L 19.4 15.6 C 20.2 15.3 21.1 15.7 21.4 16.5 L 22.6 19.5 C 22.9 20.4 22.5 21.3 21.7 21.7 C 14.5 25.5 5.5 16.5 9.3 9.3 C 9.7 8.5 10.6 8.1 11.5 8.4"
          fill="#ffffff"
        />
        <path
          d="M 13 17 L 17 21"
          stroke="#059669" strokeWidth="0.9" strokeLinecap="round"
          fill="none" opacity="0.45"
        />
      </g>
    ),
  },

  // ── Video Call: camcorder body + lens with a subtle play-triangle hint ───
  // Two-tone — white body on a sky-blue gradient (distinct from voice but
  // still a "communication" hue). The lens accent is the deeper bottom hue
  // so the lens reads as a real circular aperture, not a flat dot.
  videoCall: {
    top: '#7dd3fc', bot: '#0284c7',
    glyph: (
      <g>
        {/* Camera body */}
        <rect x="5" y="9" width="14" height="10" rx="2" fill="#ffffff" />
        {/* Lens nozzle on the right — the play-triangle hint */}
        <path d="M 19 12 L 24 9 V 19 L 19 16 Z" fill="#ffffff" />
        {/* Inset lens (camera aperture) */}
        <circle cx="11" cy="14" r="2.4" fill="#0284c7" />
        <circle cx="11" cy="14" r="1.2" fill="#ffffff" opacity="0.75" />
        {/* Tiny REC dot — top-left of body, signals "records by default" */}
        <circle cx="7.4" cy="11.4" r="0.7" fill="#ef4444" />
      </g>
    ),
  },

  // ── Google: a hand-authored multi-colour "G" arc on a clean white tile.
  // Trademark-conscious — geometrically inspired by Google's brand colour
  // palette without copying the official glyph proportions. The white tile
  // base keeps it readable on either light or dark Aqua chrome.
  googleLogo: {
    top: '#ffffff', bot: '#f1f3f4',
    glyph: (
      <g fill="none" strokeWidth="2.8" strokeLinecap="round">
        {/* Outer "G" arc, four quadrants in the recognisable Google palette */}
        <path d="M 21 14 a 7 7 0 1 1 -2.05 -4.95"  stroke="#4285F4" />
        <path d="M 21 14 a 7 7 0 0 1 -3.5 6.06"     stroke="#34A853" />
        <path d="M 17.5 20.06 a 7 7 0 0 1 -10.5 -1" stroke="#FBBC05" />
        <path d="M 7    19.06 a 7 7 0 0 1 1.05 -10" stroke="#EA4335" />
        {/* Crossbar — the right-side stub that signals "G" rather than "C" */}
        <path d="M 14.5 14 H 21" stroke="#4285F4" />
      </g>
    ),
  },

  // ── Facebook: lowercase "f" geometric letterform on Facebook-blue tile.
  // Hand-drawn glyph — vertical stem + crossbar + top arc, not a trace of
  // the official mark.
  facebookLogo: {
    top: '#3b82f6', bot: '#1d4ed8',
    glyph: (
      <g fill="#ffffff">
        {/* Vertical stem of the "f" */}
        <rect x="14.5" y="9.5" width="3" height="13.5" rx="0.4" />
        {/* Top arc — the hook that turns the stem into an "f" */}
        <path d="M 17.5 5 a 3.5 3.5 0 0 0 -3.5 3.5 V 11 H 12 V 14 H 14 V 9 a 1.5 1.5 0 0 1 1.5 -1.5 H 17.5 Z" />
        {/* Crossbar */}
        <rect x="12" y="13.4" width="6.5" height="2.6" rx="0.4" />
      </g>
    ),
  },

  // ── Manage (group participants): two head silhouettes + a plus badge ─────
  // v1.75 — sits next to voice/video in the group chat header. Indigo
  // gradient distinct from the user-management amber so it reads as
  // "manage THIS group" rather than "manage all users". The `+` badge
  // in the top-right corner cues the "add participants" action that the
  // panel surfaces.
  manage: {
    top: '#a78bfa', bot: '#7c3aed',
    glyph: (
      <g>
        {/* Two silhouettes — back person shifted up-right behind front */}
        <g fill="#ffffff">
          {/* Back head */}
          <circle cx="18" cy="9.5" r="2.6" />
          <path d="M 13.5 17 C 13.5 14.5 15.5 12.7 18 12.7 C 20.5 12.7 22.5 14.5 22.5 17 Z" />
          {/* Front head */}
          <circle cx="11.5" cy="11.5" r="3.2" />
          <path d="M 5.5 21.5 C 5.5 17.9 8.2 15.2 11.5 15.2 C 14.8 15.2 17.5 17.9 17.5 21.5 Z" />
        </g>
        {/* Plus badge — top-right corner, white plus on the deeper hue
            so it reads as a button-like affordance distinct from the
            silhouettes. */}
        <circle cx="22" cy="6.5" r="3.2" fill="#7c3aed" stroke="#ffffff" strokeWidth="1.1" />
        <path d="M 22 4.7 V 8.3 M 20.2 6.5 H 23.8" stroke="#ffffff" strokeWidth="1.1" strokeLinecap="round" />
      </g>
    ),
  },

  // ── View (details panel): an info-card sheet with three list lines ───────
  // v1.75 — sits next to voice/video. Teal gradient pairs with the call
  // family (emerald voice + sky-blue video) so the trio reads as one
  // chat-header toolset. The sheet has a folded top-right corner (the
  // classic "page" affordance) and three indented lines suggesting the
  // Media / Files / Links / Pinned sections inside the panel.
  view: {
    top: '#5eead4', bot: '#0d9488',
    glyph: (
      <g>
        {/* Page body with a small folded corner */}
        <path
          d="M 7 5 H 18 L 21 8 V 23 H 7 Z"
          fill="#ffffff"
        />
        {/* Folded corner — the deeper hue so the fold reads as the
            page-corner shadow rather than a separate shape. */}
        <path d="M 18 5 V 8 H 21 Z" fill="#0d9488" opacity="0.55" />
        {/* Three list lines — slightly different widths so the sheet
            reads as content, not a blank page. */}
        <g fill="#0d9488">
          <rect x="9.5"  y="11"   width="9"  height="1.6" rx="0.4" />
          <rect x="9.5"  y="14.2" width="7"  height="1.6" rx="0.4" />
          <rect x="9.5"  y="17.4" width="8.5" height="1.6" rx="0.4" />
        </g>
      </g>
    ),
  },
};

export default function AppIcon({ name, size = 22, className }) {
  const def = APP_ICON_DEFS[name];
  const uid = useId();
  if (!def) return null;

  // Image-source override: when the icon definition carries an `imgSrc`,
  // composite the logo INSIDE the polished squircle (via `LogoBadge`) so it
  // reads like every other AppIcon — a brand-tinted frame with the asset as
  // the glyph — instead of a bare PNG. Used by `bloomStranger`; the project
  // swaps one PNG file (`public/logo192.png`) and every nav surface updates.
  if (def.imgSrc) {
    return (
      <LogoBadge
        src={def.imgSrc}
        alt={def.alt}
        size={size}
        top={def.top}
        bot={def.bot}
        className={className}
      />
    );
  }

  const gradId = `aig-${uid}`;
  const glossId = `aih-${uid}`;
  const clipId = `aic-${uid}`;
  return (
    <svg
      viewBox="0 0 28 28"
      width={size}
      height={size}
      className={className}
      style={{ filter: 'drop-shadow(0 1px 1.5px rgba(0,0,0,0.18))' }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={def.top} />
          <stop offset="1" stopColor={def.bot} />
        </linearGradient>
        <linearGradient id={glossId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"   stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="0.6" stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="1"   stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <clipPath id={clipId}>
          <rect width="28" height="28" rx="6.5" ry="6.5" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {/* Squircle background */}
        <rect width="28" height="28" fill={`url(#${gradId})`} />
        {/* Specular glossy sheen (top half) */}
        <rect width="28" height="14" fill={`url(#${glossId})`} />
        {/* Subtle inner border for depth */}
        <rect
          x="0.5" y="0.5" width="27" height="27" rx="6.1" ry="6.1"
          fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1"
        />
        {def.glyph}
      </g>
    </svg>
  );
}

/**
 * LogoBadge — a brand/logo image composited INSIDE the polished macOS-style
 * squircle (gradient frame + glossy top-half sheen + inner border + soft
 * drop-shadow), with the image inset so the frame shows around it (the same
 * "icon inside a background" look the inline-SVG AppIcons have, but the glyph
 * is the project's logo PNG). Used for the sidebar `bloomStranger` entry and
 * the navbar logo so a bare-PNG logo matches the rest of the icon family.
 *
 * `top`/`bot` tint the frame gradient (default = Aqua accent blue; the Bloom
 * sidebar passes brand pink). `alt` makes it an accessible image; omit for a
 * decorative mark. Swap the underlying PNG (or REACT_APP_LOGO_URL) and every
 * badge updates — Rule 56 default-logo convention.
 */
export function LogoBadge({
  src,
  alt = '',
  size = 22,
  top = '#5eaaff',
  bot = '#2563eb',
  className,
}) {
  const uid = useId();
  const gradId  = `lbg-${uid}`;
  const glossId = `lbh-${uid}`;
  const clipId  = `lbc-${uid}`;
  const imgClip = `lbi-${uid}`;
  return (
    <svg
      viewBox="0 0 28 28"
      width={size}
      height={size}
      className={className}
      style={{ filter: 'drop-shadow(0 1px 1.5px rgba(0,0,0,0.18))' }}
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      focusable="false"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={top} />
          <stop offset="1" stopColor={bot} />
        </linearGradient>
        <linearGradient id={glossId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"   stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="0.6" stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="1"   stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <clipPath id={clipId}>
          <rect width="28" height="28" rx="6.5" ry="6.5" />
        </clipPath>
        <clipPath id={imgClip}>
          <rect x="3.5" y="3.5" width="21" height="21" rx="5" ry="5" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {/* Brand-tinted squircle background */}
        <rect width="28" height="28" fill={`url(#${gradId})`} />
        {/* The logo, inset so the frame reads as a border around the mark */}
        <image
          href={src}
          x="3.5" y="3.5" width="21" height="21"
          clipPath={`url(#${imgClip})`}
          preserveAspectRatio="xMidYMid slice"
        />
        {/* Specular glossy sheen (top half) — same as the glyph icons */}
        <rect width="28" height="14" fill={`url(#${glossId})`} />
        {/* Subtle inner border for depth */}
        <rect
          x="0.5" y="0.5" width="27" height="27" rx="6.1" ry="6.1"
          fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1"
        />
      </g>
    </svg>
  );
}

export { APP_ICON_DEFS };
